/**
 * FileManagerPanel
 *
 * Embedded files panel rendered inside customer/prospect detail pages.
 * Smaller, scoped variant of the global `/file-manager` page — handles
 * upload, search, paginated listing, download, delete, and an in-modal
 * viewer for a single customer's attachments.
 *
 * This file is the orchestrator only. The viewer, the viewer dialog, and
 * the delete confirm dialog live in `./file-manager-panel/`; the file-type
 * glyph comes from the shared `<FileTypeIcon>` (`./file-manager/`) that every
 * CRM file surface now uses. File-type predicates and `formatFileSize` are
 * imported from `@/lib/file-type-utils`. The public prop API
 * (`<FileManagerPanel customerId title? className?>`) and the exported
 * `FileItem` interface are unchanged so existing consumers keep working.
 */

import { useState, useEffect, useRef, useCallback } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material"
import {
  Upload,
  Download,
  Folder,
  Trash2,
} from "lucide-react"
import { useToast } from "@crm-src/hooks/use-toast"
import { customerAttachmentsApi } from "@crm-src/lib/customer-attachments-api"
import { SearchInput } from "./search-input"
import {
  isAudioFile,
  isCsvFile,
  isDocumentFile,
  isExcelFile,
  isImageFile,
  isPdfFile,
  isTextFile,
  isVideoFile,
} from "@crm-src/lib/file-type-utils"
import { FileTypeIcon } from "./file-manager/file-type-icon"
import { FileViewerDialog } from "./file-manager-panel/file-viewer-dialog"
import { DeleteFileDialog } from "./file-manager-panel/delete-file-dialog"

/**
 * Customer attachment shape returned from the
 * `/api/v1/customers/:id/attachments` endpoint. Re-exported here because
 * downstream extracted components import it from `@/components/file-manager-panel`.
 */
export interface FileItem {
  id: string
  name: string
  original_filename: string
  content_type: string
  file_size: number
  created_at: string
  uploaded_by: {
    id: string
    name: string
  }
}

interface FileManagerPanelProps {
  customerId: string
  title?: string
  className?: string
}

export function FileManagerPanel({
  customerId,
  title = "Files",
  className = "",
}: FileManagerPanelProps) {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<FileItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<FileItem | null>(null)

  // File viewer states
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  const [totalFiles, setTotalFiles] = useState(0)

  // Search state
  const [searchQuery, setSearchQuery] = useState("")

  // Load files when customerId, page, or search query changes
  useEffect(() => {
    if (customerId) {
      loadFiles()
    }
  }, [customerId, currentPage, searchQuery])

  // Search handlers passed to memoized SearchInput
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchQuery("")
    setCurrentPage(1)
  }, [])

  // Track if this is the initial load (for showing loading spinner)
  const isInitialLoadRef = useRef(true)

  const loadFiles = async () => {
    try {
      // Only show loading spinner on initial load, not on search/pagination
      if (isInitialLoadRef.current) {
        setIsLoading(true)
      }
      const response = await customerAttachmentsApi.getAttachments(customerId, {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery || undefined
      })
      const fetchedFiles = response.attachments || []
      const totalCount = response.pagination?.total_count || 0

      setFiles(fetchedFiles)
      setTotalFiles(totalCount)
      isInitialLoadRef.current = false

      // If current page is empty but there are items on previous pages, go back
      if (fetchedFiles.length === 0 && totalCount > 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1)
      }
    } catch (error: any) {
      // Ignore cancelled requests (common in React Strict Mode)
      const isCancelled = error.code === 'ERR_NETWORK_CHANGED' ||
                         error.code === 'ERR_CANCELED' ||
                         (error.message === 'Network Error' && !error.response && !error.request?.status);

      if (isCancelled) {
        // Silently ignore cancelled requests
        setIsLoading(false)
        return
      }

      console.error('Error loading files:', error)
      toast({
        title: "Error",
        description: "Failed to load files",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpload = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file
    const validation = customerAttachmentsApi.validateFile(file)
    if (!validation.valid) {
      toast({
        title: "Invalid File",
        description: validation.error,
        variant: "destructive",
      })
      return
    }

    try {
      setUploading(true)
      const response = await customerAttachmentsApi.uploadFile(customerId, file)

      if (response.success && response.attachment) {
        // Add new file to the list
        setFiles(prev => [response.attachment, ...prev])

        toast({
          title: "Success",
          description: "File uploaded successfully",
        })
      } else {
        throw new Error(response.error || "Upload failed")
      }

      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error: any) {
      console.error('Error uploading file:', error)
      const errorMessage = error.response?.data?.error || error.message || "Failed to upload file"
      toast({
        title: "Upload Failed",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (file: FileItem) => {
    try {
      await customerAttachmentsApi.downloadFile(customerId, file.id, file.original_filename)

      toast({
        title: "Download Started",
        description: `Downloading ${file.name}...`,
      })
    } catch (error: any) {
      console.error('Error downloading file:', error)
      const errorMessage = error.message || "Failed to download file"
      toast({
        title: "Download Failed",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  const handleDeleteClick = (file: FileItem) => {
    setFileToDelete(file)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return

    try {
      const response = await customerAttachmentsApi.deleteFile(customerId, fileToDelete.id)

      if (response.success) {
        // Check if this was the last item on the current page
        // If so and we're not on page 1, go to previous page
        const remainingFilesOnPage = files.filter(f => f.id !== fileToDelete.id).length
        if (remainingFilesOnPage === 0 && currentPage > 1) {
          // Go to previous page - this will trigger loadFiles via useEffect
          setCurrentPage(prev => prev - 1)
        } else {
          // Reload current page to get fresh data from server
          loadFiles()
        }

        toast({
          title: "Success",
          description: "File deleted successfully",
        })
      } else {
        throw new Error(response.error || "Delete failed")
      }
    } catch (error: any) {
      console.error('Error deleting file:', error)
      const errorMessage = error.response?.data?.error || error.message || "Failed to delete file"
      toast({
        title: "Delete Failed",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setDeleteDialogOpen(false)
      setFileToDelete(null)
    }
  }

  // Handle file click - open viewer for viewable files, otherwise download.
  const handleFileClick = (file: FileItem) => {
    if (isImageFile(file) || isPdfFile(file) || isVideoFile(file) || isAudioFile(file) ||
        isTextFile(file) || isExcelFile(file) || isCsvFile(file) || isDocumentFile(file)) {
      setSelectedFile(file)
      setIsFileViewerOpen(true)
    } else {
      // For non-viewable files, just download them
      handleDownload(file)
    }
  }

  if (isLoading) {
    return (
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Folder size={20} />
              <Typography component="span" sx={{ fontWeight: 600 }}>{title}</Typography>
            </Box>
          }
        />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={48} />
            ))}
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Folder size={20} />
                <Typography component="span" sx={{ fontWeight: 600 }}>{title}</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={<Upload size={16} />}
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </Box>
          }
        />
        <CardContent>
          {/* Memoized Search Input - isolated from parent re-renders */}
          <SearchInput
            placeholder="Search files..."
            onSearch={handleSearch}
            onClear={handleClearSearch}
            searchQuery={searchQuery}
          />

          {isLoading ? (
            <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 1 }}><Folder size={32} /></Box>
              <Typography sx={{ fontSize: 14 }}>Loading...</Typography>
            </Box>
          ) : files.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 1 }}><Folder size={32} /></Box>
              <Typography sx={{ fontSize: 14 }}>{searchQuery ? `No files found matching "${searchQuery}"` : 'No files uploaded yet'}</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {files.map((file) => (
                <Box key={file.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, border: 1, borderColor: "divider", borderRadius: 1, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}
                    onClick={() => handleFileClick(file)}
                  >
                    <Box sx={{ flexShrink: 0, display: "flex" }}>
                      <FileTypeIcon file={file} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file.name}>
                        {file.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {customerAttachmentsApi.formatFileSize(file.file_size)} • {file.uploaded_by.name}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDownload(file)
                      }}
                      sx={{ width: 32, height: 32 }}
                      title="Download file"
                    >
                      <Download size={16} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteClick(file)
                      }}
                      sx={{ width: 32, height: 32, color: "error.main", "&:hover": { color: "error.dark" } }}
                      title="Delete file"
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Pagination Controls */}
            {totalFiles > itemsPerPage && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    {currentPage} / {Math.ceil(totalFiles / itemsPerPage)}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalFiles / itemsPerPage), prev + 1))}
                    disabled={currentPage >= Math.ceil(totalFiles / itemsPerPage)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </>
          )}
          {/* Hidden file input */}
          <Box
            component="input"
            ref={fileInputRef}
            type="file"
            sx={{ display: "none" }}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
          />
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <DeleteFileDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        file={fileToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* In-modal file viewer */}
      <FileViewerDialog
        isOpen={isFileViewerOpen}
        onClose={() => setIsFileViewerOpen(false)}
        file={selectedFile}
        customerId={customerId}
        onError={(error) => {
          toast({
            title: "File Viewer Error",
            description: error,
            variant: "destructive",
          })
        }}
        onDownload={handleDownload}
      />
    </>
  )
}
